/**
 * Telematics Alerts — operator queue of unacknowledged alerts.
 *
 * Backed by /telematics/alerts. Wialon poller (bridge-agent)
 * writes; this page reads + lets the operator ack handled ones.
 *
 * Default filter is unacknowledged-only — that's the queue
 * needing attention. Filter chips switch to "Все" (all) or
 * "Обработанные" (handled-only). Per-row "Подтвердить" button
 * fires PATCH /alerts/{id}/ack and removes the row from the
 * default view.
 */

import { useCallback, useState } from "react";
import {
  AlertTriangle, ArrowDownToLine, ArrowRightLeft, Check,
  Clock, Droplets, Gauge, MapPin,
} from "lucide-react";
import { Badge, Button, Card, CardHeader } from "@vseh/ui";

import {
  telematicsApi,
  type AcknowledgedFilter,
  type AlertRuleType,
  type TelematicsAlertDto,
} from "../shared/api";
import { useFetch } from "../shared/hooks";
import {
  EmptyBlock, ErrorBlock,
} from "../shared/components/States";
import { SkeletonTable } from "../shared/components/Skeleton";
import { useT } from "../shared/i18n";


// Per-rule visual + icon. Keep here, not in the UI kit — alert
// kinds are a domain concept.
const RULE_ICON: Record<AlertRuleType, JSX.Element> = {
  geofence_exit: <MapPin    className="size-3.5" aria-hidden="true" />,
  idle_too_long: <Clock     className="size-3.5" aria-hidden="true" />,
  fuel_drop:     <Droplets  className="size-3.5" aria-hidden="true" />,
  speed_over:    <Gauge     className="size-3.5" aria-hidden="true" />,
};
const RULE_TONE: Record<AlertRuleType, "danger" | "ai" | "primary" | "heritage"> = {
  geofence_exit: "danger",       // truck wandered off-site → bad
  idle_too_long: "primary",      // fuel waste, attention-worthy
  fuel_drop:     "danger",       // potential theft → urgent
  speed_over:    "primary",      // safety
};


export function AlertsPage(): JSX.Element {
  const t = useT();
  const [ackFilter, setAckFilter] = useState<AcknowledgedFilter>("false");

  const fetcher = useCallback(
    (signal: AbortSignal) => telematicsApi.listAlerts({
      signal, acknowledged: ackFilter, limit: 200,
    }),
    [ackFilter],
  );
  const { data, error, loading, refetch } =
    useFetch<TelematicsAlertDto[]>(fetcher, [ackFilter]);

  return (
    <Card>
      <CardHeader
        title={t("alerts.page.title")}
        description={data ? String(data.length) : undefined}
        actions={
          <Button variant="ghost" size="xs" onClick={refetch}>
            {t("common.refresh")}
          </Button>
        }
      />

      <FilterTabs value={ackFilter} onChange={setAckFilter} />

      {loading && !data && <SkeletonTable rows={6} cols={4} />}
      {error && <ErrorBlock error={error} onRetry={refetch} />}
      {data && data.length === 0 && (
        <EmptyBlock
          title={t("alerts.empty.title")}
          description={t(
            ackFilter === "false"
              ? "alerts.empty.unack"
              : "alerts.empty.any",
          )}
        />
      )}

      {data && data.length > 0 && (
        <ul className="divide-y divide-base-100">
          {data.map((a) => (
            <AlertRow key={a.id} alert={a} onAcked={refetch} />
          ))}
        </ul>
      )}
    </Card>
  );
}


function FilterTabs(
  { value, onChange }: {
    value: AcknowledgedFilter;
    onChange: (v: AcknowledgedFilter) => void;
  },
): JSX.Element {
  const t = useT();
  const tabs: Array<{ id: AcknowledgedFilter; label: string }> = [
    { id: "false", label: t("alerts.tab.unack") },
    { id: "true",  label: t("alerts.tab.acked") },
    { id: "all",   label: t("alerts.tab.all") },
  ];
  return (
    <div className="mb-3 flex flex-wrap gap-1 border-b border-base-100 pb-2">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onChange(tab.id)}
          className={
            "rounded-md px-3 py-1 text-xs font-medium transition " +
            (value === tab.id
              ? "bg-ai-100 text-ai-800"
              : "text-base-600 hover:bg-base-100")
          }
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}


function AlertRow(
  { alert, onAcked }: {
    alert: TelematicsAlertDto;
    onAcked: () => void;
  },
): JSX.Element {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState<Error | null>(null);

  const onAck = useCallback(async () => {
    setBusy(true);
    setErr(null);
    try {
      await telematicsApi.ackAlert(alert.id);
      onAcked();
    } catch (e) {
      setErr(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(false);
    }
  }, [alert.id, onAcked]);

  return (
    <li className="px-3 py-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <Badge tone={RULE_TONE[alert.rule_type]} dot>
          <span className="flex items-center gap-1">
            {RULE_ICON[alert.rule_type]}
            {t(`alerts.rule.${alert.rule_type}`)}
          </span>
        </Badge>
        <span className="text-xs font-medium text-base-900">
          {t("alerts.row.vehicle", { id: alert.vehicle_id.slice(0, 8) })}
        </span>
        <span className="text-2xs text-base-500">
          {new Date(alert.occurred_at).toLocaleString("ru-RU")}
        </span>
        <div className="flex-1" />
        {alert.acknowledged_at ? (
          <Badge tone="success" dot>
            <span className="flex items-center gap-1">
              <Check className="size-3" aria-hidden="true" />
              {t("alerts.row.acked_at", {
                ts: new Date(alert.acknowledged_at).toLocaleString("ru-RU"),
              })}
            </span>
          </Badge>
        ) : (
          <Button
            variant="ai"
            size="xs"
            leftIcon={<Check className="size-3.5" />}
            loading={busy}
            onClick={onAck}
          >
            {t("alerts.row.ack")}
          </Button>
        )}
      </div>
      <DetailsBlock alert={alert} />
      {err && (
        <div className="mt-1.5 rounded-sm border border-danger-200 bg-danger-50 px-3 py-1 text-2xs text-danger-700">
          {err.message}
        </div>
      )}
    </li>
  );
}


function DetailsBlock(
  { alert }: { alert: TelematicsAlertDto },
): JSX.Element {
  const t = useT();
  const details = alert.details ?? {};

  // Render the friendly fields per rule_type. Anything we don't
  // explicitly model falls through to a compact JSON pre.
  if (alert.rule_type === "geofence_exit") {
    const zone = (details.zone_name as string) || "—";
    const lat  = details.exit_lat as number | undefined;
    const lng  = details.exit_lng as number | undefined;
    return (
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 text-2xs text-base-600">
        <span className="flex items-center gap-1">
          <ArrowRightLeft className="size-3 text-base-400" aria-hidden="true" />
          {t("alerts.details.zone")}: <span className="font-mono">{zone}</span>
        </span>
        {lat != null && lng != null && (
          <span className="flex items-center gap-1 font-mono tabular-nums">
            <MapPin className="size-3 text-base-400" aria-hidden="true" />
            {Number(lat).toFixed(5)}, {Number(lng).toFixed(5)}
          </span>
        )}
      </div>
    );
  }

  if (alert.rule_type === "idle_too_long") {
    const minutes  = details.elapsed_minutes as number | undefined;
    const distance = details.distance_m      as number | undefined;
    const since    = details.since           as string | undefined;
    return (
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 text-2xs text-base-600">
        {minutes != null && (
          <span className="flex items-center gap-1">
            <Clock className="size-3 text-base-400" aria-hidden="true" />
            {t("alerts.details.elapsed", { mins: Math.round(minutes) })}
          </span>
        )}
        {distance != null && (
          <span className="flex items-center gap-1 font-mono tabular-nums">
            <ArrowDownToLine className="size-3 text-base-400" aria-hidden="true" />
            {Math.round(distance)} m
          </span>
        )}
        {since && (
          <span className="opacity-75">
            {t("alerts.details.since")}: {new Date(since).toLocaleString("ru-RU")}
          </span>
        )}
      </div>
    );
  }

  // Fallback for fuel_drop / speed_over once they land — and for
  // any custom details we forgot to model.
  if (Object.keys(details).length === 0) return <></>;
  return (
    <pre className="mt-1.5 overflow-x-auto rounded-sm bg-base-50 px-2 py-1 font-mono text-3xs text-base-700">
      {JSON.stringify(details, null, 2)}
    </pre>
  );
}


/** Used in nav rendering — passed through unchanged so an
 *  unused-symbol lint stays quiet while we keep the icon
 *  imports paired with their rules. */
export const __ALERTS_ICON = AlertTriangle;
